import React, { ReactNode } from 'react';
import {
  VStack,
  Divider,
  Collapse,
  useDisclosure,
  Box,
} from '@wegroup/design-system';
import {
  InsurancePolicy,
  InsurancePolicyPackage,
} from '../../../../../../types/AdvisoryReport';
import InsurancePolicyPackageItemHeader from './InsurancePolicyPackageItemHeader';
import InsurancePolicyPackagePolicies from './InsurancePolicyPackagePolicies';
import InsurancePolicyPackageItemInfo from './InsurancePolicyPackageItemInfo';
import InsurancePolicyPackageItemDocuments from './InsurancePolicyPackageItemDocuments';
import AddPackageInsurancePolicyButton from './AddPackageInsurancePolicyButton';
import { Option } from '../../../../../../../../design-system/src/content/components/SearchPickerInput';
import { PartyType } from '../../../../../../../../../types/SMT';
import InsurancePolicyDocumentButton from '../../shared/insurance-policy/InsurancePolicyDocumentButton';
import { useTranslation } from 'react-i18next';

interface Props {
  data: InsurancePolicyPackage;
  partyType: PartyType;
  partyId?: string;
  withActions?: boolean;
  controlled?: boolean;
  customRiskObjects?: Option[];
  customActions?: ReactNode;
  withDocuments?: boolean;

  // Actions
  onEdit?: (policyPackage: InsurancePolicyPackage) => void;
  onRemove?: (id: string) => void;
  onEditPolicy?: (insurancePolicy: InsurancePolicy) => void;
  onAddPolicy?: (insurancePolicy: InsurancePolicy) => void;
  onRemovePolicy?: (id: string, packageId?: string) => void;
}

const InsurancePolicyPackageItem: React.FC<Props> = ({
  data,
  partyType,
  customActions,
  partyId,
  withActions,
  controlled,
  withDocuments,
  customRiskObjects,
  onEdit,
  onRemove,
  onEditPolicy,
  onAddPolicy,
  onRemovePolicy,
}) => {
  const { t } = useTranslation();
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
  return (
    <VStack
      mb="2"
      borderRadius="md"
      bg="gray.ultraLightBg"
      border="1px"
      px="4"
      borderColor="gray.50"
      boxShadow="level1"
      align="stretch"
    >
      <InsurancePolicyPackageItemHeader
        data={data}
        isOpen={isOpen}
        onToggle={onToggle}
        controlled={controlled}
        onEdit={onEdit}
        onRemove={onRemove}
        withActions={withActions}
        customActions={customActions}
      />
      <Divider borderColor="gray.50" />
      <InsurancePolicyPackageItemInfo data={data} />
      <Collapse in={isOpen}>
        <Box position="relative" pb="4">
          {withDocuments && <InsurancePolicyPackageItemDocuments data={data} />}
          <InsurancePolicyPackagePolicies
            insurancePolicies={data.insurance_policies}
            partyType={partyType}
            controlled={controlled}
            withActions={withActions}
            onEdit={onEditPolicy}
            onRemove={onRemovePolicy}
            customRiskObjects={customRiskObjects}
            withDocuments={withDocuments}
          />
          {withActions && (
            <AddPackageInsurancePolicyButton
              customRiskObjects={customRiskObjects}
              controlled={controlled}
              onAdd={onAddPolicy}
              packageData={data}
            />
          )}
          {data.policy_package_document_link && (
            <Box mt="2">
              <InsurancePolicyDocumentButton
                documentUrl={data.policy_package_document_link}
                isPackage
                partyId={partyId}
                title={t('INSURANCE-POLICIES_PACKAGE-DOCUMENT_BUTTON')}
              />
            </Box>
          )}
        </Box>
      </Collapse>
    </VStack>
  );
};

export default InsurancePolicyPackageItem;
